import {FC, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, Stack, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {DownloadIcon} from '../../icons/DownloadIcon';
import AMPModal from '../../common/AMPModal';
import {chargersApi} from 'src/api';
import {ChargePointSearchRequestData, ExportFileType} from 'src/types';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {isErrorObject} from 'src/utils/helper';
import {CancelButton} from 'src/components/common/CancelButton';
import dayjs from 'dayjs';

interface ChargerExportButtonProps {
    filters: ChargePointSearchRequestData;
}

const downloadButtonSx = {
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    button: {
        height: '36px',
        px: 1.5,
        minWidth: 'auto',
        textTransform: 'none',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'neutral.200',
        backgroundColor: 'common.white',
        color: 'neutral.500',
        boxShadow: 'none',
        gap: 0.5,
        '&:hover': {
            borderColor: 'neutral.300',
            backgroundColor: 'common.white',
            boxShadow: 'none',
        },
    },
    btnText: {
        fontSize: '14px',
        fontWeight: 500,
        color: 'inherit',
    },
};

const modalContentSx = {
    padding: '32px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

const actionsWrapperSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    padding: 2,
    borderTop: '1px solid',
    borderColor: 'divider',
};

export const ChargerExportButton: FC<ChargerExportButtonProps> = ({filters}) => {
    // The control is the action: without the permission the server refuses it, so showing it
    // offers a button whose only outcome is an error.
    const canDoIt = useCan('chargers.export');
    const intl = useIntl();
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [exportChargers] = chargersApi.useExportChargersMutation();

    const handleShowModal = (status: boolean) => () => {
        setShowModal(status);
    };

    const handleExport = async (fileType: ExportFileType) => {
        try {
            setIsLoading(true);

            const blob = await exportChargers({
                data: {
                    ...filters,
                    fileType,
                },
            }).unwrap();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
            const fileName = `chargers_${timestamp}.${fileType.toLowerCase()}`;
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            NotifySuccess(intl.formatMessage({id: 'downloadSuccess'}) || 'Download started successfully');
            handleShowModal(false)();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message || 'Export failed');
            } else {
                NotifyError('Export failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (!canDoIt) {
        return null;
    }

    return (
        <Box sx={downloadButtonSx.root}>
            <Button
                onClick={handleShowModal(true)}
                startIcon={<DownloadIcon/>}
                sx={downloadButtonSx.button}
            >
                <Typography
                    variant="body1"
                    sx={downloadButtonSx.btnText}
                >
                    {intl.formatMessage({id: 'export'})}
                </Typography>
            </Button>
            <AMPModal
                handleClose={handleShowModal(false)}
                open={showModal}
                title={intl.formatMessage({id: 'export'})}
                type="sm"
                fullWidth
                withoutPaddings
            >
                <Box sx={modalContentSx}>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="outlined"
                            onClick={() => handleExport(ExportFileType.CSV)}
                            disabled={isLoading}
                            sx={{minWidth: 100}}
                        >
                            CSV
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => handleExport(ExportFileType.PDF)}
                            disabled={isLoading}
                            sx={{minWidth: 100}}
                        >
                            PDF
                        </Button>
                    </Stack>
                </Box>
                <Box sx={actionsWrapperSx}>
                    <CancelButton
                        text="cancel"
                        handler={handleShowModal(false)}
                    />
                </Box>
            </AMPModal>
        </Box>
    );
};